import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Target, Euro, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Btn, eur } from "../components/common";
import ProposalDrawer from "../components/ProposalDrawer";
import { useAuth } from "../auth";
import { useAutoRefresh } from "../lib/autoRefresh";

const STAGES = [
  { key: "novo", label: "Novo", cls: "bg-slate-50 border-slate-200", prob: 10 },
  { key: "qualificado", label: "Qualificado", cls: "bg-sky-50 border-sky-200", prob: 40 },
  { key: "proposta", label: "Proposta", cls: "bg-amber-50 border-amber-200", prob: 60 },
  { key: "negociacao", label: "Negociacao", cls: "bg-orange-50 border-orange-200", prob: 80 },
  { key: "ganho", label: "Ganho", cls: "bg-emerald-50 border-emerald-200", prob: 100 },
];

export default function DealsKanban() {
  const nav = useNavigate();
  const { can } = useAuth();
  const isSuperAdmin = can("*");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const [previewProp, setPreviewProp] = useState(null);
  const [cleaning, setCleaning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/crm/deals");
      setRows(data || []);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useAutoRefresh(load);

  const cols = STAGES.map((s) => ({ ...s, items: rows.filter((r) => (r.estagio || "novo") === s.key) }));

  const onDrop = async (stage) => {
    if (!drag || drag.estagio === stage) { setDrag(null); setOver(null); return; }
    setRows((prev) => prev.map((x) => x.id === drag.id ? { ...x, estagio: stage } : x));
    const meta = STAGES.find((s) => s.key === stage);
    try {
      const { data } = await api.put(`/crm/deals/${drag.id}`, { estagio: stage, probabilidade: meta?.prob });
      if (data?.auto_proposal) {
        const p = data.auto_proposal;
        toast.success(`${drag.nome} -> PROPOSTA - rascunho ${p.reused ? "existente" : "criado"}: ${p.numero}`, {
          action: {
            label: "Pre-visualizar",
            onClick: () => setPreviewProp({ id: p.id, numero: p.numero, titulo: p.titulo, account_id: drag.account_id || "", estado: "rascunho" }),
          },
          duration: 10000,
        });
      } else {
        toast.success(`${drag.nome} -> ${stage.toUpperCase()}`);
      }
      if (stage === "ganho" || stage === "perdido") load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      load();
    } finally { setDrag(null); setOver(null); }
  };

  const totalMrr = (items) => items.reduce((s, r) => s + (r.mrr || 0), 0);

  const limparFantasmas = async () => {
    setCleaning(true);
    try {
      const { data } = await api.post("/crm/deals/cleanup-ghost-leads");
      toast.success(`${data.deals_removidos} oportunidade(s) fantasma removida(s), ${data.contas_promovidas} conta(s) promovida(s) a Oportunidade.`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setCleaning(false); }
  };

  return (
    <>
      <PageHeader
        overline="Vendas"
        title="Funil de Vendas"
        action={
          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <Btn variant="ghost" onClick={limparFantasmas} disabled={cleaning} testid="deals-cleanup-ghosts-btn">
                <Trash2 size={14} /> {cleaning ? "A limpar..." : "Limpar leads sem Oportunidade"}
              </Btn>
            )}
            <Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Atualizar</Btn>
          </div>
        }
      />

      <div className="text-xs text-muted2 mb-3">Arraste os deals entre estagios. A probabilidade e atualizada automaticamente.</div>

      {loading && <div className="text-center text-muted2 py-6">A carregar...</div>}

      <div className="grid grid-cols-5 gap-3 min-h-[60vh]" data-testid="deals-board">
        {cols.map((c) => (
          <div key={c.key}
            data-testid={`deals-col-${c.key}`}
            onDragOver={(e) => { e.preventDefault(); setOver(c.key); }}
            onDragLeave={() => setOver((s) => s === c.key ? null : s)}
            onDrop={() => onDrop(c.key)}
            className={`rounded-lg border-2 ${over === c.key ? "border-blue border-dashed bg-blue/5" : c.cls} p-2 flex flex-col`}
          >
            <div className="flex items-center justify-between mb-2 px-1">
              <div className="text-[11px] font-bold uppercase tracking-wider text-navy">{c.label}</div>
              <span className="text-[10px] font-mono bg-white border border-tint rounded px-1.5">{c.items.length}</span>
            </div>
            <div className="text-[10px] text-slate-500 mb-2 px-1">MRR total: {eur(totalMrr(c.items))}</div>
            <div className="flex-1 space-y-1.5 overflow-y-auto max-h-[65vh]">
              {c.items.length === 0 && <div className="text-[11px] text-slate-400 italic text-center py-4">Vazio</div>}
              {c.items.map((r) => (
                <div key={r.id}
                  draggable
                  onDragStart={() => setDrag(r)}
                  onDragEnd={() => setDrag(null)}
                  data-testid={`deal-card-${r.id}`}
                  className="bg-white border border-tint rounded-md p-2 cursor-move hover:border-blue hover:shadow-sm transition"
                >
                  <div className="text-sm font-semibold text-navy truncate flex items-center gap-1"><Target size={11} /> {r.nome}</div>
                  {r.plataforma && <div className="text-[9px] font-mono text-blue mt-0.5">{r.plataforma}</div>}
                  <div className="flex items-center justify-between mt-1.5">
                    <div className="text-[11px] text-slate-500 truncate">{r.probabilidade || 0}%</div>
                    <div className="text-sm font-bold text-navy inline-flex items-center gap-0.5"><Euro size={10} />{eur(r.mrr || r.valor_unico || 0)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      {previewProp && (
        <ProposalDrawer
          proposal={previewProp}
          open={!!previewProp}
          onClose={() => setPreviewProp(null)}
          accounts={[]}
          onOpenPdf={async (p) => {
            try {
              const { data } = await api.get(`/cpq/proposals/${p.id}/pdf`, { responseType: "blob" });
              window.open(URL.createObjectURL(data), "_blank");
            } catch { toast.error("Nao foi possivel abrir o PDF."); }
          }}
          onOpenEditor={(p) => { setPreviewProp(null); nav(`/app/propostas?edit=${p.id}`); }}
        />
      )}
    </>
  );
}
